import React, { useEffect, useState } from 'react';
import { createStyles, Dialog, DialogActions, DialogTitle } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { HighlightOffRounded } from '@material-ui/icons';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import RefuseReturnIcon from '../../icons/RefuseReturn-icon';
import {
  ActionTakenType,
  DocumentTemplateType,
  INumberedDocumentView,
} from '../../../api-client/autogenerated';
import { getPageTitle } from '../../../scripts/utils';
import { useSelector } from 'react-redux';
import { getDocumentState } from '../../../features/document/selectors';
import { getDocumentsType } from '../../../features/documents/selectors';
import PackageListTable, { SubmittalInfo } from './PackageListTable';

interface RefusePackageDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  packageDocuments: INumberedDocumentView[];
  isConsultantReview?: boolean;
  initialState?: SubmittalInfo[];
  submit?: (review: SubmittalInfo[]) => void;
  selectedDocument?: INumberedDocumentView;
}

const useStyles = makeStyles(() =>
  createStyles({
    rootIconButton: {
      padding: '0px 8px 0px 0px',
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    packageName: {
      fontFamily: 'Roboto',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '26px',
      lineHeight: '30px',
      color: '#0947B9', // Blue 700
    },
    separateButton: {
      border: '2px solid #0947B9',
      boxSizing: 'border-box',
      borderRadius: '4px',
      color: '#0947B9', // Red 300
      width: '122px',
      height: '32px',
    },
    dialogue: {
      maxWidth: '735px',
    },
  }),
);

export default function RefusePackageDialog(props: RefusePackageDialogProps) {
  const classes = useStyles();
  const {
    isOpen,
    handleClose,
    packageDocuments,
    isConsultantReview,
    initialState,
    submit,
    selectedDocument,
  } = props;

  const parentDocument = useSelector(getDocumentState);
  const documentTypeState = useSelector(getDocumentsType)!;

  const documentType =
    documentTypeState === DocumentTemplateType.AsBuilt
      ? DocumentTemplateType.AsBuiltPackages
      : documentTypeState;

  const [submittalInfo, setSubmittalInfo] = useState<SubmittalInfo[]>(initialState || []);

  useEffect(() => {
    if (initialState?.length) setSubmittalInfo(initialState);
    else
      setSubmittalInfo(
        packageDocuments.map((doc) => {
          return {
            document: doc,
            actionTaken: undefined,
            comment: '',
          };
        }),
      );
  }, [packageDocuments]);

  const handleActionTakenChange = (event: any, index: number) => {
    const newInfo = [...submittalInfo];
    newInfo[index].actionTaken = event.target.value as ActionTakenType;
    setSubmittalInfo(newInfo);
  };

  const handleCommentChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    const newInfo = [...submittalInfo];
    newInfo[index].comment = event.target.value;
    setSubmittalInfo(newInfo);
  };

  const handleRemoveComment = (commentId: string, index: number) => {
    const newInfo = [...submittalInfo];
    newInfo[index].existingComments = newInfo[index].existingComments?.filter(
      (c) => c.id !== commentId,
    );
    setSubmittalInfo(newInfo);
  };

  const getAction = () => {
    const architectSubmitDisabled = !submittalInfo.every(({ actionTaken }) => actionTaken);
    return (
      <Button
        variant="outlined"
        onClick={() => {
          if (submit) submit(submittalInfo);
          handleClose();
        }}
        disabled={architectSubmitDisabled}
        className={classes.separateButton}
      >
        Submit
      </Button>
    );
  };

  const closeClicked = () => {
    handleClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={closeClicked}
      PaperProps={{ style: { minWidth: 720 } }}
      fullWidth
      classes={{ paper: classes.dialogue }}
    >
      <DialogTitle
        style={{
          display: 'flex',
          flexDirection: 'column',
          whiteSpace: 'nowrap',
          paddingBottom: '0px',
        }}
      >
        <Typography className={classes.packageName}>
          {getPageTitle(documentType, parentDocument || selectedDocument)}
        </Typography>
        {!isConsultantReview && (
          <Typography style={{ marginTop: 4, marginBottom: 12 }}>
            <RefuseReturnIcon />
            <b style={{ color: '#464546', paddingLeft: '8px' }}>Refuse/Return Partial Package</b>
          </Typography>
        )}
        <IconButton
          onClick={closeClicked}
          style={{ right: '14px', top: '14px', position: 'absolute' }}
          classes={{
            root: classes.rootIconButton,
          }}
        >
          <HighlightOffRounded />
        </IconButton>
      </DialogTitle>
      <PackageListTable
        submittalInfo={submittalInfo}
        handleActionTakenChange={handleActionTakenChange}
        handleCommentChange={handleCommentChange}
        handleRemoveComment={handleRemoveComment}
      />
      <DialogActions style={{ margin: 16, flexDirection: 'column', alignItems: 'center' }}>
        {getAction()}
      </DialogActions>
    </Dialog>
  );
}
